import type { ChatStreamChunk } from '../../services/chatService';

type CompactConversationEntry = {
    kind: string;
    type?: string;
    contentType?: string;
    content?: string;
    body?: unknown;
};

export class ConversationResponseBuffer {
    private _chunks: ChatStreamChunk[] = [];

    public add(chunk: ChatStreamChunk): void {
        this._chunks.push(cloneChatStreamChunk(chunk));
    }

    public addUserMessage(content: string): void {
        const trimmedContent = content.trim();
        if (!trimmedContent.length) {
            return;
        }

        this._chunks.push({
            type: 'json',
            contentType: 'user_message',
            done: false,
            status: 200,
            threadId: undefined,
            body: {
                content: trimmedContent
            }
        });
    }

    public clear(): void {
        this._chunks = [];
    }

    public toJson(): string {
        return formatConversationJsonResponses(this._chunks);
    }
}

export function formatConversationJsonResponses(chunks: ChatStreamChunk[]): string {
    return JSON.stringify(toCompactConversationEntries(chunks), null, 2);
}

function cloneChatStreamChunk(chunk: ChatStreamChunk): ChatStreamChunk {
    return {
        type: chunk.type,
        contentType: chunk.contentType,
        done: chunk.done,
        status: chunk.status,
        threadId: chunk.threadId,
        body: cloneDebugValue(chunk.body)
    };
}

function cloneDebugValue(value: unknown): unknown {
    if (value === null) {
        return null;
    }

    const valueType = typeof value;
    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
        return value;
    }

    if (valueType === 'undefined') {
        return null;
    }

    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return String(value);
    }
}

function toCompactConversationEntries(chunks: ChatStreamChunk[]): CompactConversationEntry[] {
    const entries: CompactConversationEntry[] = [];
    let pendingAssistantMessage: CompactConversationEntry | null = null;

    const flushPendingAssistantMessage = () => {
        if (!pendingAssistantMessage) {
            return;
        }

        entries.push(pendingAssistantMessage);
        pendingAssistantMessage = null;
    };

    for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];

        if (chunk.type === 'text' && typeof chunk.body === 'string') {
            if (!pendingAssistantMessage) {
                pendingAssistantMessage = {
                    kind: 'assistant_message',
                    content: ''
                };
            }

            pendingAssistantMessage.content = `${pendingAssistantMessage.content ?? ''}${chunk.body}`;
            continue;
        }

        flushPendingAssistantMessage();

        if (chunk.contentType === 'status') {
            const statusText = resolveStatusText(chunk.body);
            if (statusText.length > 0 && shouldSkipExpandedStatus(entries, statusText)) {
                continue;
            }

            entries.push({
                kind: 'status',
                type: chunk.type,
                contentType: chunk.contentType,
                content: statusText,
                body: chunk.body
            });
            continue;
        }

        if (chunk.contentType === 'user_message') {
            const messageText = resolveUserMessageText(chunk.body);
            if (!messageText.length) {
                continue;
            }

            const previousEntry = entries[entries.length - 1];
            if (previousEntry?.kind === 'user_message' && (previousEntry.content ?? '').trim() === messageText) {
                continue;
            }

            entries.push({
                kind: 'user_message',
                type: chunk.type,
                contentType: chunk.contentType,
                content: messageText,
                body: chunk.body
            });
            continue;
        }

        entries.push({
            kind: chunk.contentType === 'task_plan' ? 'task_plan' : chunk.contentType ?? chunk.type ?? 'chunk',
            type: chunk.type,
            contentType: chunk.contentType,
            body: chunk.body
        });
    }

    flushPendingAssistantMessage();

    return entries;
}

function shouldSkipExpandedStatus(entries: CompactConversationEntry[], currentStatusText: string): boolean {
    const previousEntry = entries[entries.length - 1];
    if (!previousEntry || previousEntry.kind !== 'status') {
        return false;
    }

    const previousStatusText = (previousEntry.content ?? '').trim();
    if (!previousStatusText.length) {
        return false;
    }

    return currentStatusText.startsWith(previousStatusText);
}

function resolveStatusText(body: unknown): string {
    if (typeof body === 'string') {
        return body.trim();
    }

    if (!body || typeof body !== 'object') {
        return '';
    }

    const record = body as Record<string, unknown>;
    const description = typeof record.description === 'string' ? record.description.trim() : '';
    if (description.length > 0) {
        return description;
    }

    if (typeof record.status === 'string') {
        return record.status.trim();
    }

    return '';
}

function resolveUserMessageText(body: unknown): string {
    if (typeof body === 'string') {
        return body.trim();
    }

    if (!body || typeof body !== 'object') {
        return '';
    }

    const record = body as Record<string, unknown>;
    if (typeof record.content === 'string') {
        return record.content.trim();
    }

    if (typeof record.text === 'string') {
        return record.text.trim();
    }

    return '';
}
